import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/utils';
import { buttonDownSound } from '@/utils/sound';

// Play the shared button click effect when pressed
const buttonVariants = cva(
	'group relative overflow-hidden cursor-pointer select-none inline-flex items-center justify-center whitespace-nowrap rounded-xl text-sm font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:translate-y-[1px] active:shadow-none',
	{
		variants: {
			variant: {
				default: 'border border-2 border-border bg-background/80 text-card-foreground hover:bg-card/90 focus-visible:ring-primary/25',
				primary:
					'text-primary-foreground border border-primary/75 bg-primary shadow-[0_14px_26px_-12px_color-mix(in_srgb,var(--primary-shadow)_70%,transparent)] hover:bg-primary/90 focus-visible:ring-primary/45 before:pointer-events-none before:absolute before:inset-[1px] before:rounded-[inherit] before:bg-[linear-gradient(180deg,rgba(255,255,255,0.62),rgba(255,255,255,0))] before:opacity-75 before:transition-opacity before:content-[""] hover:before:opacity-95 active:before:opacity-65 after:pointer-events-none after:absolute after:inset-0 after:rounded-[inherit] after:border after:border-white/5 after:opacity-50',
				secondary:
					'text-secondary-foreground border border-secondary/60 bg-secondary/90 hover:bg-secondary focus-visible:ring-secondary/40 before:pointer-events-none before:absolute before:inset-[1px] before:rounded-[inherit] before:bg-[linear-gradient(180deg,rgba(255,255,255,0.45),rgba(255,255,255,0))] before:opacity-60 before:transition-opacity before:content-[""] hover:before:opacity-85',
				accent:
					'text-accent-foreground border border-accent/60 bg-accent/90 hover:bg-accent focus-visible:ring-accent/35 before:pointer-events-none before:absolute before:inset-[1px] before:rounded-[inherit] before:bg-[linear-gradient(180deg,rgba(255,255,255,0.5),rgba(255,255,255,0))] before:opacity-60 before:transition-opacity before:content-[""] hover:before:opacity-85',
				success:
					'text-success-foreground border border-success/60 bg-success/90 hover:bg-success focus-visible:ring-success/35 before:pointer-events-none before:absolute before:inset-[1px] before:rounded-[inherit] before:bg-[linear-gradient(180deg,rgba(255,255,255,0.45),rgba(255,255,255,0))] before:opacity-55 before:transition-opacity before:content-[""] hover:before:opacity-80',
				destructive:
					'text-destructive-foreground border border-destructive/70 bg-destructive/90 hover:bg-destructive focus-visible:ring-destructive/40 before:pointer-events-none before:absolute before:inset-[1px] before:rounded-[inherit] before:bg-[linear-gradient(180deg,rgba(255,255,255,0.35),rgba(255,255,255,0))] before:opacity-55 before:transition-opacity before:content-[""] hover:before:opacity-75',
				outline: 'border border-border bg-transparent text-card-foreground hover:bg-card/10 focus-visible:ring-primary/25',
				ghost: 'border border-transparent bg-transparent text-card-foreground hover:bg-card/10 focus-visible:ring-primary/20',
				link: 'text-primary underline-offset-4 hover:underline border-0 focus-visible:ring-0',
			},
			size: {
				default: 'px-6 py-3',
				sm: 'px-3 py-2 text-sm',
				lg: 'px-8 py-4 text-lg',
				icon: 'h-5 w-5',
			},
			shadow: {
				none: '',
				default: 'shadow-lg active:shadow-none',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default',
			shadow: 'none',
		},
	},
);

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
	asChild?: boolean;
	isLoading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, shadow, asChild = false, isLoading, children, onMouseDown, onMouseUp, ...props }, ref) => {
		const Comp = asChild ? Slot : 'button';

		const handleMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
			buttonDownSound.play();
			if (onMouseDown) onMouseDown(event);
		};

		const handleMouseUp = (event: React.MouseEvent<HTMLButtonElement>) => {
			if (onMouseUp) onMouseUp(event);
		};

		return (
			<Comp
				className={cn(
					buttonVariants({
						variant,
						size,
						shadow,
						className: cn(className, props.disabled && 'pointer-events-none opacity-50'),
					}),
				)}
				ref={ref}
				disabled={isLoading || props.disabled}
				onMouseDown={handleMouseDown}
				onMouseUp={handleMouseUp}
				{...props}
			>
				{children}
			</Comp>
		);
	},
);
Button.displayName = 'Button';

export { Button, buttonVariants };
